
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
const VehicleManagementModal = ({
apiCall,
vehicles: initialVehicles = [],
onClose,
onSuccess,
showError,
showSuccess
}) => {
const [vehicles, setVehicles] = useState(initialVehicles);
const [newVehicle, setNewVehicle] = useState({
vehicleNumber: '',
vehicleType: '',
fuelType: 'Gasoline',
category: 'School Bus'
});
const [loading, setLoading] = useState(false);
const [deletingId, setDeletingId] = useState(null);
const [loadingVehicles, setLoadingVehicles] = useState(false);
const fuelTypes = ['Gasoline', 'Diesel', 'Electric', 'Hybrid'];
const categories = ['School Bus', 'Priests', 'Other'];

// Load vehicles on mount
useEffect(() => {
    loadVehicles();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const loadVehicles = async () => {
    try {
        setLoadingVehicles(true);
        const data = await apiCall('/gas/vehicles');
        setVehicles(data || []);
    } catch (error) {
        console.error('Error loading vehicles:', error);
        showError('Error loading vehicles: ' + error.message);
    } finally {
        setLoadingVehicles(false);
    }
};

const handleAddVehicle = async (e) => {
    e.preventDefault();
    if (!newVehicle.vehicleNumber.trim() || !newVehicle.vehicleType.trim()) {
        showError('Please fill in all required fields');
        return;
    }

    try {
        setLoading(true);
        await apiCall('/gas/vehicles', {
            method: 'POST',
            body: JSON.stringify({
                vehicleNumber: newVehicle.vehicleNumber.trim().toUpperCase(), 
                vehicleType: newVehicle.vehicleType.trim(),
                fuelType: newVehicle.fuelType,
                category: newVehicle.category
            })
        });
        
        showSuccess(`Vehicle ${newVehicle.vehicleNumber} added successfully.`);
        setNewVehicle({
            vehicleNumber: '',
            vehicleType: '',
            fuelType: 'Gasoline',
            category: 'School Bus'
        });
        
        // Reload vehicles list
        await loadVehicles();
        onSuccess(); // Triggers data refresh in parent component
    } catch (error) {
        showError(error.message);
    } finally {
        setLoading(false);
    }
};

const handleDeleteVehicle = async (vehicleId, vehicleNumber) => {
    if (!window.confirm(`Are you sure you want to permanently delete vehicle ${vehicleNumber}? This action cannot be undone.`)) {
        return;
    }

    try {
        setDeletingId(vehicleId);
        await apiCall(`/gas/vehicles/${vehicleId}`, {
            method: 'DELETE'
        });
        
        showSuccess(`Vehicle ${vehicleNumber} has been successfully deleted.`);
        
        // Reload vehicles list
        await loadVehicles();
        onSuccess(); // Triggers data refresh in parent component
    } catch (error) {
        showError(error.message);
    } finally {
        setDeletingId(null);
    }
};

return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-xl font-bold text-gray-800">Vehicle Management</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                    <X size={24} />
                </button>
            </div>
            
            <div className="p-6 space-y-6">
                
                {/* Add New Vehicle Form */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Plus size={20} />
                        Add New Vehicle
                    </h4>
                    <form onSubmit={handleAddVehicle} className="space-y-4">
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Vehicle Number */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Vehicle Number *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={newVehicle.vehicleNumber}
                                    onChange={(e) => setNewVehicle({...newVehicle, vehicleNumber: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., BUS-001"
                                />
                            </div>
                            {/* Vehicle Type */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Vehicle Type *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={newVehicle.vehicleType}
                                    onChange={(e) => setNewVehicle({...newVehicle, vehicleType: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Toyota Coaster"
                                />
                            </div>
                            {/* Fuel Type */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Fuel Type *
                                </label>
                                <select
                                    required
                                    value={newVehicle.fuelType}
                                    onChange={(e) => setNewVehicle({...newVehicle, fuelType: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    {fuelTypes.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Category */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Category *
                                </label>
                                <select
                                    required
                                    value={newVehicle.category}
                                    onChange={(e) => setNewVehicle({...newVehicle, category: e.target.value})}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors"
                        >
                            {loading ? 'Adding...' : 'Add Vehicle'}
                        </button>
                    </form>
                </div>

                {/* Existing Vehicles List */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-bold text-gray-800">
                            Existing Vehicles ({vehicles.length})
                        </h4>
                        <button
                            onClick={loadVehicles}
                            disabled={loadingVehicles}
                            className="text-sm text-blue-600 hover:text-blue-700 font-semibold disabled:opacity-50"
                        >
                            {loadingVehicles ? 'Refreshing...' : '🔄 Refresh'}
                        </button>
                    </div>
                    
                    {loadingVehicles ? (
                        <div className="flex items-center justify-center py-12 border border-gray-200 rounded-lg">
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                                <p className="text-gray-600 text-sm">Loading vehicles...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Vehicle #</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Fuel Type</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Category</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {vehicles.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                                No vehicles found. Add your first vehicle above.
                                            </td>
                                        </tr>
                                    ) : (
                                        vehicles.map(vehicle => (
                                            <tr key={vehicle.VehicleID} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">{vehicle.VehicleNumber}</td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{vehicle.VehicleType}</td>
                                                <td className="px-4 py-3">
                                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                                        {vehicle.FuelType || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
                                                        {vehicle.Category || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                        vehicle.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                        {vehicle.IsActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <button
                                                        onClick={() => handleDeleteVehicle(vehicle.VehicleID, vehicle.VehicleNumber)}
                                                        disabled={deletingId === vehicle.VehicleID}
                                                        className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-wait transition-colors"
                                                        title={`Delete ${vehicle.VehicleNumber}`}
                                                    >
                                                        {deletingId === vehicle.VehicleID ? (
                                                            <span className="inline-block animate-spin">⏳</span>
                                                        ) : (
                                                            <Trash2 size={18} />
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                    onClick={onClose}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                    Close
                </button>
            </div>
        </div>
    </div>
);
};
export default VehicleManagementModal;